import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { dashboardApi } from '@/services/api'
import type { DashboardOverview } from '@/types'
import { Users, AlertCircle, Plus, Building2, TrendingUp, ArrowRight, ExternalLink } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'

export default function FirmDashboard() {
  const { currentFirm, isLoading: authLoading, createFirm } = useAuth()
  const [data, setData] = useState<DashboardOverview | null>(null)
  const [loading, setLoading] = useState(false)
  const [firmName, setFirmName] = useState('')
  const [creatingFirm, setCreatingFirm] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!currentFirm) return
    setLoading(true)
    dashboardApi.overview(currentFirm.id).then((res) => {
      setData(res.data)
    }).catch(() => {
      setData({ total_clients: 0, active_clients: 0, archived_clients: 0, clients: [] })
    }).finally(() => setLoading(false))
  }, [currentFirm?.id])

  if (authLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} size="dashboard">
              <CardContent className="p-5">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!currentFirm) {
    return (
      <div className="max-w-md mx-auto mt-16">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 bg-[#0078C8]/10 rounded-[12px] flex items-center justify-center mx-auto mb-5">
              <Building2 className="w-7 h-7 text-[#0078C8]" />
            </div>
            <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Welcome to LedgerPro</h2>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-6">
              Create your accounting firm to start managing client ledgers.
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!firmName.trim()) return
                setCreatingFirm(true)
                try {
                  await createFirm(firmName.trim())
                  toast.success('Firm created!')
                } catch {
                  toast.error('Failed to create firm')
                } finally {
                  setCreatingFirm(false)
                }
              }}
              className="space-y-3"
            >
              <Input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                placeholder="e.g. Smith & Associates"
                required
              />
              <Button type="submit" disabled={creatingFirm} className="w-full">
                {creatingFirm ? 'Creating...' : 'Create Firm'}
              </Button>
            </form>

            <div className="mt-5 pt-5 border-t border-[#CCCED2]">
              <p className="text-[12px] text-[rgba(0,10,30,0.75)] mb-2">Invited as a client viewer?</p>
              <Link to="/portal" className="flex items-center justify-center gap-1.5 text-[13px] text-[#0078C8] hover:underline font-bold">
                <ExternalLink className="w-3.5 h-3.5" />
                Go to Client Portal
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} size="dashboard">
              <CardContent className="p-5">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!data) return null

  const unreconciledTotal = data.clients.reduce((s, c) => s + c.unreconciled_lines, 0)

  const stats = [
    {
      label: 'Total Clients',
      value: data.total_clients,
      icon: Users,
      colorClass: 'bg-[#0078C8]/10 text-[#0078C8]',
    },
    {
      label: 'Active Clients',
      value: data.active_clients,
      icon: TrendingUp,
      colorClass: 'bg-[#D4EDDA] text-[#00823C]',
    },
    {
      label: 'Unreconciled Items',
      value: unreconciledTotal,
      icon: AlertCircle,
      colorClass: unreconciledTotal > 0 ? 'bg-[#FFF3CD] text-[#856404]' : 'bg-[#D4EDDA] text-[#00823C]',
    },
  ]

  const filteredClients = data.clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Page header - Xero style */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[21px] font-bold text-[#000A1E] leading-[32px]">Business Overview</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">Overview of your client portfolio</p>
        </div>
        <Link to="/clients/new" className={cn(buttonVariants({ variant: 'create' }), 'gap-2')}>
          <Plus className="w-4 h-4" /> New Client
        </Link>
      </div>

      {/* Stat cards - Xero dashboard card style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label} size="dashboard" className="hover:shadow-card-hover transition-shadow duration-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{stat.label}</p>
                  <p className="text-[28px] font-bold text-[#000A1E] mt-1 tabular-nums">{stat.value}</p>
                </div>
                <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center flex-shrink-0 ${stat.colorClass}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Client table - Xero content panel style */}
      <Card>
        <CardHeader className="border-b border-[#CCCED2] px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-[17px]">Clients</CardTitle>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-48"
              />
              <Link to="/clients" className="flex items-center gap-1 text-[13px] text-[#0078C8] font-bold hover:underline">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Bank Accounts</TableHead>
                <TableHead className="text-right">Unreconciled</TableHead>
                <TableHead>Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>
                    <Link
                      to={`/clients/${client.id}/dashboard`}
                      className="font-bold text-[#0078C8] hover:underline"
                    >
                      {client.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={client.status === 'active' ? 'success' : 'default'}>
                      {client.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[rgba(0,10,30,0.75)] capitalize">{client.plan_tier}</TableCell>
                  <TableCell className="text-right text-[rgba(0,10,30,0.75)]">{client.total_bank_accounts}</TableCell>
                  <TableCell className="text-right">
                    {client.unreconciled_lines > 0 ? (
                      <Badge variant="warning">
                        {client.unreconciled_lines}
                      </Badge>
                    ) : (
                      <span className="text-[#00823C] font-bold">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[rgba(0,10,30,0.75)]">
                    {client.last_activity
                      ? new Date(client.last_activity).toLocaleDateString()
                      : <span className="text-[#CCCED2]">&mdash;</span>}
                  </TableCell>
                </TableRow>
              ))}
              {filteredClients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <Users className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
                    <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
                      {search ? 'No clients match your search' : 'No clients yet'}
                    </p>
                    {!search && (
                      <Link to="/clients/new" className="text-[13px] text-[#0078C8] hover:underline font-bold mt-1 inline-block">
                        Create your first client
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
